import React, { useEffect, useState, useContext, useRef } from "react";

import { useNavigate, Navigate, useLocation, Link } from "react-router-dom";

import { FormInputs } from "../../components/inputs/FormInputs";

import { UserContext } from "../../context/user.context";
import { CartContext } from "../../context/cart.context";
import { toast } from "react-hot-toast";

import { requestOTP, verifyOTP } from "../../utils/zulalWebAPI";
import { useAuth } from "../../context/auth.context";
import { fetchCurrentUser } from "../../utils/SonicAPIV2";

export const UserLogin = () => {
  const [buttonLable, setButtonLable] = useState("Get OTP");
  const [OTPdisable, setOTPDisable] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef(null);
  const { setCurrentUser } = useContext(UserContext);
  const { refreshAuth, authData, authLoading } = useAuth();

  const { setCartItems } = useContext(CartContext);

  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  //console.log(`fromPage State value: ${fromPage}`);
  const fromPage = location.state?.fromPage || "dashboard";

  // Remove leading slashes and ensure the path includes the `/user` prefix
  const normalizedFromPage = fromPage.startsWith("/user")
    ? fromPage
    : `/user/${fromPage.replace(/^\/+/, "")}`;

  // Console output for debugging
  // console.log(`fromPage normalized: ${normalizedFromPage}`);

  // Map of page titles
  const pageTitles = {
    "/user/checkout": "Checkout",
    "/user/complaintpage": "Suggestions and Complaints",
    "/user/dashboard": "Dashboard",
  };
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  useEffect(() => {
    if (!authData || !authData.IsActive) return;

    // Extract fromPath and default to '/user/dashboard' if undefined
    const fromPath = location.state?.from?.pathname || "/user/dashboard";

    // Normalize the target route, ensuring all secure pages are within /user
    const validRoutes = [
      "/user/checkout",
      "/user/complaint",
      "/user/dashboard",
    ];
    const normalizedTarget = validRoutes.includes(fromPath)
      ? fromPath
      : "/user/dashboard";

    // Perform navigation
    navigate(normalizedTarget, {
      replace: true,
      state: { PageTitle: "Dashboard" },
    });
  }, [authData?.IsActive, location.state, navigate]);

  function startCooldown(seconds = 60) {
    setCooldown(seconds);
    // Ensure button label is in Verify state while cooldown runs
    if (seconds > 0) setButtonLable("Verify OTP");

    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          // cooldown ended: clear interval and reset UI states
          if (cooldownRef.current) {
            clearInterval(cooldownRef.current);
            cooldownRef.current = null;
          }
          // revert button label to initial state
          setButtonLable("Get OTP");
          // disable OTP input because the OTP has expired / cooldown done
          setOTPDisable(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  const [values, setValues] = useState({
    phone: "",
    password: "",
  });

  const input = [
    {
      id: 1,
      name: "phone",
      type: "text",
      placeholder: "501234567",
      pattern: "^[0-9]{9,9}$",
      errorMessage: "Enter 9 digits of Mobile Number",
      label: "",
      required: true,
    },
    {
      id: 2,
      name: "password",
      type: "password",
      placeholder: "Please enter 6 Digits OTP",
      pattern: "^[0-6]{6,6}*$",
      errorMessage: "Enter 6 Digits OTP",
      label: "",
      required: true,
    },
  ];

  const onChange = (e) => {
    setValues({ ...values, [e.target.name]: e.target.value });
    setErrorMessage("");
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    try {
      if (buttonLable === "GET OTP" || buttonLable === "Get OTP") {
        if (values.phone === "" || values.phone.length < 9) {
          toast.error("Please enter a valid phone number");
          return;
        }
        // call helper

        const result = await requestOTP(values.phone);

        // console.log("Request OTP response:", result);

        // if fail
        if (!result || !result.success) {
          const msg = result?.message || "Failed to request OTP";
          toast.error(msg);
          setErrorMessage(msg);
          startCooldown(5); // small cooldown to slow attempts
          return;
        }

        // success
        toast.success(result?.message || "OTP requested successfully");
        setOTPDisable(false);
        setButtonLable("Verify OTP");
        startCooldown(60);
      }
    } catch (err) {
      // structured ApiError from helper
      if (err && err.isApiError) {
        if (err.status === 429) {
          const wait = err.retryAfter || 60;
          toast.error(
            err.message + " Try after an hour" ||
              `Too many requests. Try again in ${wait}s`
          );
          startCooldown(wait);
          setOTPDisable(true);
          setButtonLable("Get OTP");
        } else if (err.status === 404) {
          // endpoint not found (if this came from server) — but 404 here likely means phone not registered or bad path
          toast.error(err.message || "Resource not found");
        } else if (err.status >= 400 && err.status < 500) {
          toast.error(
            err.message ||
              "Request error: " + (err.data?.message || "Bad request")
          );
        } else if (err.status >= 500) {
          toast.error("Server error. Please try again later.");
        } else {
          toast.error(err.message || "Network error. Try again");
        }
      } else {
        console.error("Unexpected error requesting OTP", err);
        toast.error("Unexpected error. Please try again.");
        startCooldown(5); // gentle fallback cooldown
      }
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();

    try {
      if (!values.password || values.password.length < 6) {
        toast.error("Please enter the OTP");
        return;
      }

      setLoading?.(true); // if you have a loading state, enable it

      // 1) Call server to verify OTP. Server should set httpOnly cookie on success.
      const resp = await verifyOTP(values.phone, values.password);

      // console.log("Verify OTP response:", resp);
      // console.log("-------------------");

      if (!resp || !resp.success) {
        // Server responded but indicates failure
        const msg = resp?.message || "OTP verification failed";
        toast.error(msg);
        // allow retry; keep OTP input enabled
        setOTPDisable?.(false);
        setButtonLable?.("Verify OTP");
        startCooldown?.(5);
        return;
      }

      // Success path
      toast.success(resp.message || "OTP verified");
      setCartItems?.([]); // clear cart as before

      //get the Promo Product List

      // 2) Refresh auth state from AuthContext (populates authData using httpOnly cookie)
      const result = await refreshAuth();

      if (!result.success) {
        toast.error("Failed to refresh authentication. Please try again.");
        // allow retry; keep OTP input enabled
        setOTPDisable?.(false);
        setButtonLable?.("Verify OTP");
        return;
      }
      // 3) Fetch current user profile from server

      const customerDetails = await fetchCurrentUser();

      setCurrentUser((prev) => {
        if (
          JSON.stringify(prev.customerDetails) ===
          JSON.stringify(customerDetails)
        ) {
          return prev; // Avoid unnecessary updates
        }
        return { ...prev, customerDetails: customerDetails };
      });

      // 5) Reset OTP UI state and navigate to fromPage
      setOTPDisable?.(true);
      setButtonLable?.("Get OTP");

      // console.log("************* checking fromPage value *************");
      // console.log(fromPage);
      // console.log("****************************************************");

      const pageTitle = pageTitles[normalizedFromPage] || "Dashboard";

      // Navigate to the target route
      navigate(normalizedFromPage, {
        replace: true,
        state: { PageTitle: pageTitle },
      });
    } catch (err) {
      // structured ApiError handling
      if (err && err.isApiError) {
        if (err.status === 429) {
          const wait = err.retryAfter || 60;
          toast.error(
            err.message || `Too many requests. Try again in ${wait}s`
          );
          startCooldown?.(wait);
          setOTPDisable?.(true);
          setButtonLable?.("Get OTP");
        } else if (err.status === 404) {
          toast.error(err.message || "Resource not found");
          setOTPDisable?.(false);
          setButtonLable?.("Get OTP");
        } else if (err.status >= 400 && err.status < 500) {
          toast.error(
            err.message ||
              `Request error: ${err.data?.message || "Bad request"}`
          );
          setOTPDisable?.(false);
          setButtonLable?.("Verify OTP");
        } else if (err.status >= 500) {
          toast.error("Server error. Please try again later.");
        } else {
          toast.error(err.message || "Network error. Try again");
        }
      } else {
        console.error("Unexpected error verifying OTP", err);
        toast.error("Unexpected error. Please try again.");
        startCooldown?.(5); // gentle fallback cooldown
        setOTPDisable?.(false);
        setButtonLable?.("Verify OTP");
      }
    } finally {
      setLoading?.(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (buttonLable === "Get OTP") {
      await handleRequestOTP(e);
    } else if (buttonLable === "Verify OTP") {
      await handleVerifyOTP(e);
    }
  };

  return (
    <div>
      <div className="bg-pagePattern bg-center">
        <div className="flex mx-auto max-w-screen-lg text-center justify-center items-center mb-9 h-32  ">
          <label className="text-7xl text-textWhite font-medium  ">
            Login using OTP
          </label>
        </div>
      </div>

      <div className="mx-auto max-w-screen-sm text-center justify-center items">
        <div className="mx-auto max-w-sm ml-4 mr-4 mt-4 justify-center items-center">
          <form onSubmit={handleSubmit}>
            <div className="text-left">
              <span className="text-2xl text-primary font-poppins font-semibold">
                Account Login
              </span>
            </div>
            <div className="flex mx-auto ml-4 mr-4 mt-4 justify-center items-center">
              {/* <span className="hidden">Welcome Message : {outletName} </span> */}
            </div>
            <div className="mx-auto max-w-screen-sm text-center justify-center">
              <div className="flex flex-row mt-2 ">
                <div
                  className="bg-darkVeryLight ring-1 ring-secondary h-10 px-4 bg-zinc-100 rounded-tl-md rounded-bl-md 
              justify-start items-center gap-4 inline-flex"
                >
                  {/* <PhoneIcon className="text-darkTextColor w-6" /> */}
                  <div className="font-normal text-P14 w-8">+971</div>
                </div>

                <div className="mx-auto justify-center w-full">
                  <FormInputs
                    autoFocus={true}
                    value={values[input.name]}
                    errorMessage={errorMessage}
                    {...input[0]}
                    onChange={onChange}
                  />
                </div>
              </div>

              <div className="flex flex-row mt-2 ">
                <div
                  className="bg-darkVeryLight ring-1 ring-secondary h-10 px-4 bg-zinc-100 rounded-tl-md rounded-bl-md 
              justify-start items-center gap-4 inline-flex"
                >
                  {/* <LockClosedIcon className="w-6 text-darkTextColor" /> */}
                  <div className="font-normal text-P14 w-8">OTP</div>
                </div>

                <div className="mx-auto justify-center w-full">
                  <FormInputs
                    disabled={OTPdisable}
                    value={values[input.name]}
                    {...input[1]}
                    onChange={onChange}
                  />
                </div>
              </div>

              <div className=" flex flex-row mt-2  text-center justify-center">
                <span className="text-gray-500 text-3xl ">
                  {cooldown > 0
                    ? `00:${String(cooldown).padStart(2, "0")}`
                    : "00:00"}
                </span>
              </div>
            </div>

            <div className="mx-auto justify-center mt-4 items-center">
              <button
                id="buttonSubmit"
                name="buttonSubmit"
                type="submit"
                value={buttonLable}
                className="bg-secondary h-14 rounded-md w-full text-textWhite
                font-poppins text-base hover:bg-secondaryHover"
              >
                {buttonLable}
              </button>
              <span className="text-textRed font-bold text-read text-C12">
                {errorMessage}
              </span>

              <div className="mt-2 text-gray-500 text-read text-base">
                Not Registered with us ? &nbsp;
                <Link
                  to={"../createuser"}
                  className="font-medium text-primary dark:text-blue-500 hover:underline"
                  state={{ PageTitle: "New User" }}
                >
                  New User
                </Link>
              </div>
            </div>
          </form>
          <div className="mb-80"></div>
        </div>
      </div>
    </div>
  );
};
